import { Alert, Box, Button, TextField, Typography, Stack } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FormikProvider, useFormik, Form } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import useHandleApiResponse from '../../../../../Hooks/useHandleApiResponse';
import { courseSync, getLensSyncData } from '../../../../../redux/features/lensSyncSlice';
import { useSelector } from 'react-redux';

export default function CourseSyncModal({ row, onClose }) {
  const dispatch = useDispatch();
  const handleApiResponse = useHandleApiResponse();

  const { loading } = useSelector((state) => state.lens);
  const [confirmed, setConfirmed] = useState(false);

  const isAlreadySynced = Boolean(row?.wise_course_classroom_id);

  const handleConfirm = () => {
    setConfirmed(true);
  };

  // ✅ Initial Values
  const initialValues = {
    courseName: row?.course_name || '',
    subjectName: row?.subject_name || '',
    subjectEnrollmentId: row?.subject_enrollment_id,
  };

  // ✅ Validation
  const validationSchema = Yup.object({
    courseName: Yup.string().required('Course is required'),
    subjectName: Yup.string().required('Subject is required'),
  });

  // ✅ Formik
  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      const payload = {
        // ...values,
        subjectEnrollmentId: values.subjectEnrollmentId,
      };
      dispatch(courseSync(payload)).then((res) => {
        handleApiResponse(res, {
          onSuccess: () => {
            onClose();
          },
          onFailed: () => {
            console.log('Course sync Failed');
          },
        });
      });
    },
  });

  if (!row) return null;

  return (
    <Box p={2}>
      <Typography fontWeight={600} mb={2}>
        Student: {row?.student_name}
      </Typography>

      {!confirmed ? (
        <>
          {isAlreadySynced ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Notice</strong>
              <br />
              This course is already synced.
              <br />
              Re-syncing may update course details.
            </Alert>
          ) : (
            <Alert severity="success" sx={{ mb: 2 }}>
              <strong>Disclaimer</strong>
              <br />
              This will sync the course.
            </Alert>
          )}

          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button onClick={() => onClose(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleConfirm}>
              {isAlreadySynced ? 'Proceed' : 'Continue'}
            </Button>
          </Box>
        </>
      ) : (
        <FormikProvider value={formik}>
          <Form onSubmit={formik.handleSubmit}>
            <Stack spacing={2}>
              {/* COURSE */}
              <TextField
                label="Course Name"
                name="courseName"
                fullWidth
                value={formik.values.courseName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.courseName && Boolean(formik.errors.courseName)}
                helperText={formik.touched.courseName && formik.errors.courseName}
              />

              {/* SUBJECT */}
              <TextField
                label="Subject Name"
                name="subjectName"
                fullWidth
                value={formik.values.subjectName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.subjectName && Boolean(formik.errors.subjectName)}
                helperText={formik.touched.subjectName && formik.errors.subjectName}
              />

              {/* ACTIONS */}
              <Stack direction="row" justifyContent="flex-end" spacing={2}>
                <Button onClick={() => setConfirmed(false)}>Back</Button>
                <Button variant="contained" type="submit" disabled={!formik.isValid || loading}>
                  {' '}
                  Sync
                </Button>
              </Stack>
            </Stack>
          </Form>
        </FormikProvider>
      )}
    </Box>
  );
}
